import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Download, Image } from 'lucide-react'
import { formatFrameFilename } from '@/lib/utils'
import type { ExportFormat } from '@/lib/frame-export-utils'

interface FrameExportOptionsProps {
  onExport: (format: ExportFormat, quality?: number) => void
  disabled?: boolean
  frameNumber?: number
  videoName?: string | null
  estimatedSize?: number // in bytes
}

export function FrameExportOptions({
  onExport,
  disabled = false,
  frameNumber,
  videoName,
}: FrameExportOptionsProps) {
  const [format, setFormat] = useState<ExportFormat>('png')
  const [jpegQuality, setJpegQuality] = useState(92)
  const [isOpen, setIsOpen] = useState(false)

  const handleExport = () => {
    onExport(format, format === 'jpeg' ? jpegQuality / 100 : undefined)
    setIsOpen(false)
  }

  const filename = frameNumber ? formatFrameFilename(frameNumber, videoName) : 'frame.png'
  const extension = format === 'jpeg' ? '.jpg' : '.png'
  const displayFilename = filename.replace('.png', extension)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="gap-2">
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="font-medium flex items-center gap-2">
            <Image className="h-4 w-4" />
            Export Frame
          </div>

          <div className="space-y-3">
            <Label>Format</Label>
            <RadioGroup
              value={format}
              onValueChange={(v) => setFormat(v as ExportFormat)}
              className="grid grid-cols-2 gap-2"
            >
              <Label
                htmlFor="png"
                className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value="png" id="png" />
                <div>
                  <div className="font-medium">PNG</div>
                  <div className="text-xs text-muted-foreground">Lossless, larger</div>
                </div>
              </Label>
              <Label
                htmlFor="jpeg"
                className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
              >
                <RadioGroupItem value="jpeg" id="jpeg" />
                <div>
                  <div className="font-medium">JPEG</div>
                  <div className="text-xs text-muted-foreground">Smaller file</div>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {format === 'jpeg' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Quality</Label>
                <span className="text-sm font-mono text-muted-foreground">{jpegQuality}%</span>
              </div>
              <Slider
                value={[jpegQuality]}
                onValueChange={([v]) => setJpegQuality(v)}
                min={50}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Smaller</span>
                <span>Higher quality</span>
              </div>
            </div>
          )}

          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-3 truncate" title={displayFilename}>
              Filename: {displayFilename}
            </p>
            <Button onClick={handleExport} className="w-full gap-2">
              <Download className="h-4 w-4" />
              Download {format.toUpperCase()}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

